import { defineStore } from 'pinia';
import KanbanApi from 'dashboard/api/kanban';
import { recusaDeOutroAberto } from 'dashboard/components-next/Kanban/helpers/recusaDoMover';

// CRM-01h: time, responsável e prioridade aceitam vários valores; etiqueta e "na etapa há N+
// dias" são novos. O servidor aceita lista nos três (e ainda o valor único de antes).
export const emptyFilters = () => ({
  inbox_ids: [],
  status: [],
  team_id: [],
  assignee_id: [],
  priority: [],
  labels: [],
  stale_days: null,
  q: '',
});

// CRM-04 R2-T7 (K2): a linha do tempo do painel. Mensagem e evento têm ids de tabelas diferentes, e a
// mesma linha pode voltar na borda de duas páginas.
const timelineVazia = () => ({
  cardId: null,
  items: [],
  nextCursor: null,
  hiddenCount: 0,
  loading: false,
  loadingMore: false,
  error: false,
});

const chaveDoItem = item => `${item.type}:${item.id}`;

// O que o aviso `kanban.card.updated` muda no painel aberto. Os `contacts` ficam de fora: no painel é a
// lista inteira, com os campos; no aviso, só até 3.
const CAMPOS_DO_NEGOCIO = [
  'title',
  'display_name',
  'value_cents',
  'owner',
  'aguardando',
  'aguardando_desde',
  'ultima_mensagem_em',
  'proximo_passo',
  'stage_entered_at',
  'days_in_stage',
  'contacts_count',
  'conversations_count',
];

// A face do card (§6.2) a partir do painel: até 3 contatos, no formato da face, e o total.
const AVATARES_NA_FACE = 3;
const faceDoPainel = painel => {
  const contatos = painel.contacts || [];
  const face = {
    contacts: contatos.slice(0, AVATARES_NA_FACE).map(contato => ({
      id: contato.id,
      name: contato.name,
      avatar_url: contato.avatar_url || contato.thumbnail || '',
    })),
    contacts_count: painel.contacts_count ?? contatos.length,
  };
  CAMPOS_DO_NEGOCIO.forEach(campo => {
    if (campo in painel) face[campo] = painel[campo];
  });
  return face;
};

export const useKanbanStore = defineStore('kanban', {
  state: () => ({
    pipelines: [],
    activePipelineId: null,
    // { [stageId]: { cards: [], totalCount: 0, nextCursor: null, loadingMore: false } }
    columns: {},
    filters: emptyFilters(),
    // CRM-01h fase 3: o que o "mover em bloco" vai levar. `cards` é { [cardId]: stageId } (os
    // marcados um a um); `stageIds` são as etapas inteiras, que o servidor resolve pelos filtros,
    // inclusive os cards que a coluna ainda não carregou.
    selection: { cards: {}, stageIds: [] },
    // O lote em andamento (ou o último, até a tela dar o recado): o que o servidor devolve.
    bulkMove: null,
    // Payload of the card detail drawer; null when it is closed.
    details: null,
    // RN-02: o último mover recusado porque um contato do negócio já tem outro aberto no funil,
    // `{ cardId, otherCardId, contactName }`. O painel desse negócio mostra o motivo e o caminho.
    moveRefusal: null,
    timeline: timelineVazia(),
    // Account-wide contact field layout, shared by the drawer and the field manager.
    fieldLayout: [],
    uiFlags: {
      fetchingPipelines: false,
      fetchingBoard: false,
      movingCard: false,
      fetchingDetails: false,
      generatingSummary: false,
    },
  }),

  getters: {
    activePipeline: state =>
      state.pipelines.find(p => p.id === state.activePipelineId) || null,

    stages() {
      return this.activePipeline?.stages || [];
    },

    cardsForStage: state => stageId => state.columns[stageId]?.cards || [],

    countForStage: state => stageId => state.columns[stageId]?.totalCount || 0,

    isStageSelected: state => stageId =>
      state.selection.stageIds.includes(stageId),

    isCardSelected: state => card =>
      state.selection.stageIds.includes(card.stage_id) ||
      Boolean(state.selection.cards[card.id]),

    // Etapa inteira conta pelo total do filtro (inclusive o não carregado); card marcado numa
    // etapa inteira já está contado nela.
    selectedCount(state) {
      const { cards, stageIds } = state.selection;
      const avulsos = Object.values(cards).filter(
        stageId => !stageIds.includes(stageId)
      ).length;
      return (
        avulsos +
        stageIds.reduce(
          (soma, stageId) => soma + this.countForStage(stageId),
          0
        )
      );
    },

    hasSelection: state =>
      Boolean(
        state.selection.stageIds.length ||
        Object.keys(state.selection.cards).length
      ),

    // Sum of card values per stage, in cents. Only meaningful when the pipeline
    // has show_value turned on.
    valueForStage: state => stageId =>
      (state.columns[stageId]?.cards || []).reduce(
        (total, card) => total + (card.value_cents || 0),
        0
      ),

    // Only send filters that are actually set — an empty array would serialise as
    // `inbox_ids[]=` and match nothing on the server.
    queryParams: state => {
      const params = {};
      Object.entries(state.filters).forEach(([key, value]) => {
        if (Array.isArray(value) ? value.length : value) params[key] = value;
      });
      return params;
    },
  },

  actions: {
    async fetchPipelines() {
      this.uiFlags.fetchingPipelines = true;
      try {
        const { data } = await KanbanApi.getPipelines();
        this.pipelines = data.payload || [];
        if (!this.activePipelineId && this.pipelines.length) {
          const preferred =
            this.pipelines.find(p => p.is_default) || this.pipelines[0];
          this.activePipelineId = preferred.id;
        }
        return this.pipelines;
      } finally {
        this.uiFlags.fetchingPipelines = false;
      }
    },

    async fetchBoard(pipelineId = this.activePipelineId) {
      if (!pipelineId) return;
      this.uiFlags.fetchingBoard = true;
      try {
        const { data } = await KanbanApi.getBoard(pipelineId, this.queryParams);
        this.applyPipeline(data.pipeline);
        const columns = {};
        (data.columns || []).forEach(column => {
          columns[column.stage_id] = {
            cards: column.cards || [],
            totalCount: column.total_count || 0,
            nextCursor: column.next_cursor || null,
            loadingMore: false,
          };
        });
        this.columns = columns;
      } finally {
        this.uiFlags.fetchingBoard = false;
      }
    },

    applyPipeline(pipeline) {
      if (!pipeline) return;
      const index = this.pipelines.findIndex(p => p.id === pipeline.id);
      if (index === -1) this.pipelines.push(pipeline);
      else this.pipelines[index] = pipeline;
      this.activePipelineId = pipeline.id;
    },

    async setActivePipeline(pipelineId) {
      this.activePipelineId = pipelineId;
      this.columns = {};
      this.clearSelection();
      await this.fetchBoard(pipelineId);
    },

    // Trocar o filtro limpa a seleção: "todos desta etapa" seria outro conjunto de cards.
    async setFilters(filters) {
      this.filters = { ...this.filters, ...filters };
      this.clearSelection();
      await this.fetchBoard();
    },

    // Troca os filtros sem buscar o quadro: a tela aplica os da URL (ou os lembrados) antes da
    // primeira busca, para não pedir o quadro duas vezes.
    loadFilters(filters) {
      this.filters = { ...emptyFilters(), ...filters };
    },

    async resetFilters() {
      this.filters = emptyFilters();
      this.clearSelection();
      await this.fetchBoard();
    },

    // CRM-01h fase 3: "Carregar mais" — a próxima página da coluna, pelo cursor do servidor.
    async loadMore(stageId) {
      const column = this.columns[stageId];
      if (!column?.nextCursor || column.loadingMore) return;
      column.loadingMore = true;
      try {
        const { data } = await KanbanApi.getStageCards(
          this.activePipelineId,
          stageId,
          { cursor: column.nextCursor, filters: this.queryParams }
        );
        const tem = new Set(column.cards.map(card => card.id));
        column.cards.push(...(data.cards || []).filter(c => !tem.has(c.id)));
        column.nextCursor = data.next_cursor || null;
      } finally {
        column.loadingMore = false;
      }
    },

    // Card de uma etapa selecionada inteira já está no lote: a caixa dele não desmarca sozinha.
    toggleCard(card) {
      if (this.selection.stageIds.includes(card.stage_id)) return;
      const cards = { ...this.selection.cards };
      if (cards[card.id]) delete cards[card.id];
      else cards[card.id] = card.stage_id;
      this.selection.cards = cards;
    },

    toggleStage(stageId) {
      const { stageIds, cards } = this.selection;
      if (stageIds.includes(stageId)) {
        this.selection.stageIds = stageIds.filter(id => id !== stageId);
        return;
      }
      this.selection.stageIds = [...stageIds, stageId];
      this.selection.cards = Object.fromEntries(
        Object.entries(cards).filter(([, cardStage]) => cardStage !== stageId)
      );
    },

    clearSelection() {
      this.selection = { cards: {}, stageIds: [] };
    },

    // Os filtros vão só com etapa inteira: são eles que dizem quais cards "todos desta etapa" é.
    async startBulkMove({ toStageId, applyRules }) {
      const { cards, stageIds } = this.selection;
      const payload = {
        pipeline_id: this.activePipelineId,
        to_stage_id: toStageId,
        card_ids: Object.keys(cards).map(Number),
        stage_ids: stageIds,
        apply_rules: Boolean(applyRules),
      };
      if (stageIds.length) payload.filters = this.queryParams;
      const { data } = await KanbanApi.createBulkMove(payload);
      this.bulkMove = data;
      return data;
    },

    async refreshBulkMove() {
      if (!this.bulkMove?.id) return null;
      const { data } = await KanbanApi.getBulkMove(this.bulkMove.id);
      this.bulkMove = data;
      return data;
    },

    // CRM-04 (RN-02): a recusa de "outro negócio aberto" fica guardada para o painel do card dizer o motivo e
    // oferecer o caminho; o mover que passa a apaga. A falha chega inteira a quem chamou.
    async moveCard(payload) {
      try {
        await this.moverCard(payload);
      } catch (error) {
        const recusa = recusaDeOutroAberto(error);
        this.moveRefusal = recusa
          ? { cardId: payload.cardId, ...recusa }
          : null;
        throw error;
      }
      if (this.moveRefusal?.cardId === payload.cardId) this.moveRefusal = null;
    },

    // Optimistic move: the card jumps immediately and only rolls back if the API
    // rejects it. Dragging a card and waiting 300ms for it to land feels broken.
    async moverCard({ cardId, fromStageId, toStageId, newIndex }) {
      const source = this.columns[fromStageId];
      const target = this.columns[toStageId];
      const cardIndex = source
        ? source.cards.findIndex(c => c.id === cardId)
        : -1;
      // CRM-04 C7: o seletor do painel move card que o quadro não carregou (filtrado, depois do
      // "Carregar mais", de outro funil). Sem onde dar o pulo otimista, vai direto ao servidor;
      // antes voltava calado e o card não saía do lugar.
      if (!target || cardIndex === -1) {
        await KanbanApi.moveCard(cardId, {
          stageId: toStageId,
          beforeId: null,
          afterId: null,
        });
        // Sem vizinho, o servidor põe o card no topo da etapa. Com a etapa no quadro, ele relê em vez
        // de adivinhar: fora das colunas, o card pode estar depois do "Carregar mais" (e conta no
        // total) ou fora do filtro (e não conta), e daqui não dá para saber qual (CRM-04 TELA-1).
        if (target) await this.fetchBoard();
        return;
      }
      const [card] = source.cards.splice(cardIndex, 1);
      const snapshot = { ...card };

      target.cards.splice(newIndex, 0, card);
      card.stage_id = toStageId;
      if (fromStageId !== toStageId) {
        source.totalCount = Math.max(0, source.totalCount - 1);
        target.totalCount += 1;
      }

      const beforeId = target.cards[newIndex - 1]?.id || null;
      const afterId = target.cards[newIndex + 1]?.id || null;

      this.uiFlags.movingCard = true;
      try {
        const { data } = await KanbanApi.moveCard(cardId, {
          stageId: toStageId,
          beforeId,
          afterId,
        });
        Object.assign(card, data);
      } catch (error) {
        // Put it back where it came from and resync with the server.
        const revertIndex = target.cards.findIndex(c => c.id === cardId);
        if (revertIndex !== -1) target.cards.splice(revertIndex, 1);
        source.cards.splice(cardIndex, 0, snapshot);
        if (fromStageId !== toStageId) {
          source.totalCount += 1;
          target.totalCount = Math.max(0, target.totalCount - 1);
        }
        throw error;
      } finally {
        this.uiFlags.movingCard = false;
      }
    },

    // Another agent moved a card — reconcile without a full board refetch.
    applyRemoteMove({ id, pipeline_id: pipelineId, stage_id: stageId }) {
      // O painel aberto acompanha, de qualquer funil: o seletor seguia na etapa antiga quando outra
      // pessoa ou o PowerAgent movia o card, e o próximo movimento partia de lá (CRM-04 TELA-1).
      if (this.details?.id === id) this.details.stage_id = stageId;
      if (pipelineId !== this.activePipelineId) return;

      let moved = null;
      Object.keys(this.columns).forEach(key => {
        const column = this.columns[key];
        const index = column.cards.findIndex(c => c.id === id);
        if (index === -1) return;
        if (Number(key) === stageId) {
          moved = 'same';
          return;
        }
        [moved] = column.cards.splice(index, 1);
        column.totalCount = Math.max(0, column.totalCount - 1);
      });

      if (!moved || moved === 'same') return;
      const target = this.columns[stageId];
      if (!target) return;
      moved.stage_id = stageId;
      target.cards.unshift(moved);
      target.totalCount += 1;
    },

    // CRM-04 R2-T7: `kanban.card.updated`, com os campos da face (o "aguardando" muda a cada mensagem,
    // os contatos e as conversas mudam no painel de outra pessoa). Remenda o card no quadro e o painel
    // aberto. Devolve true quando o painel aberto precisa reler o negócio: entrou ou saiu um contato ou
    // uma conversa, e a lista dele ficou velha.
    applyRemoteUpdate(card) {
      if (!card?.id) return false;
      if (card.stage_id && card.pipeline_id) this.applyRemoteMove(card);
      this.patchCardEverywhere(card.id, card);
      if (this.details?.id !== card.id) return false;

      const reler = ['contacts_count', 'conversations_count'].some(
        campo => campo in card && card[campo] !== this.details[campo]
      );
      CAMPOS_DO_NEGOCIO.forEach(campo => {
        if (campo in card) this.details[campo] = card[campo];
      });
      return reler;
    },

    // O aviso inteiro: remenda e, quando a lista do painel aberto ficou velha, relê o negócio.
    async receiveCardUpdate(card) {
      const reler = this.applyRemoteUpdate(card);
      if (reler) await this.refreshDeal(card.id);
      return reler;
    },

    // CRM-04 R2-T7 (N15): `kanban.card.merged` ou o juntar desta tela. O absorvido sai das colunas, da
    // contagem e da seleção. Devolve true quando era ele no painel, que fecha: quem chama abre o que fica.
    applyMerge({ absorbed_id: absorvido }) {
      Object.values(this.columns).forEach(column => {
        const index = column.cards.findIndex(card => card.id === absorvido);
        if (index === -1) return;
        column.cards.splice(index, 1);
        column.totalCount = Math.max(0, column.totalCount - 1);
      });
      if (this.selection.cards[absorvido]) {
        const cards = { ...this.selection.cards };
        delete cards[absorvido];
        this.selection.cards = cards;
      }
      if (this.details?.id !== absorvido) return false;
      this.closeDetails();
      return true;
    },

    // Os negócios do funil para o "Juntar com…", pela busca do quadro (o título e os contatos), sem
    // mexer nas colunas da tela.
    async searchDeals(pipelineId, q) {
      const params = { per_stage: 10 };
      if (q) params.q = q;
      const { data } = await KanbanApi.getBoard(pipelineId, params);
      return (data.columns || []).flatMap(column => column.cards || []);
    },

    // Só o administrador, e o servidor recusa o resto. O card da rota fica, e o servidor o devolve.
    async mergeCards(cardId, absorvidoId) {
      const { data } = await KanbanApi.mergeCards(cardId, absorvidoId);
      // RN-02: juntado o outro aberto, o motivo da recusa do mover já não vale.
      if (this.moveRefusal?.cardId === cardId) this.moveRefusal = null;
      this.applyMerge({ survivor_id: cardId, absorbed_id: absorvidoId });
      if (data?.id === cardId) this.patchCardEverywhere(cardId, data);
      return data;
    },

    async createPipeline(payload) {
      const { data } = await KanbanApi.createPipeline(payload);
      this.pipelines.push(data);
      return data;
    },

    async updatePipeline(id, payload) {
      const { data } = await KanbanApi.updatePipeline(id, payload);
      this.applyPipeline(data);
      return data;
    },

    async deletePipeline(id) {
      await KanbanApi.deletePipeline(id);
      this.pipelines = this.pipelines.filter(p => p.id !== id);
      if (this.activePipelineId === id) {
        this.activePipelineId = this.pipelines[0]?.id || null;
        this.columns = {};
        if (this.activePipelineId) await this.fetchBoard();
      }
    },

    async createStage(payload) {
      await KanbanApi.createStage(this.activePipelineId, payload);
      await this.fetchBoard();
    },

    async updateStage(id, payload) {
      await KanbanApi.updateStage(id, payload);
      await this.fetchBoard();
    },

    async deleteStage(id, moveToStageId) {
      await KanbanApi.deleteStage(id, moveToStageId);
      await this.fetchBoard();
    },

    async reorderStages(stages) {
      await KanbanApi.reorderStages(this.activePipelineId, stages);
      await this.fetchBoard();
    },

    async fetchFieldLayout() {
      const { data } = await KanbanApi.getFieldLayout();
      this.applyFieldLayout(data.fields || []);
      return this.fieldLayout;
    },

    async saveFieldLayout(keys) {
      const { data } = await KanbanApi.saveFieldLayout(keys);
      this.applyFieldLayout(data.fields || []);
      return this.fieldLayout;
    },

    // The drawer renders from its own copy of the layout, taken when the card was opened.
    // Both fetch and save have to push into it, or a field created while the drawer is
    // open only shows up after a page reload.
    applyFieldLayout(fields) {
      this.fieldLayout = fields;
      if (this.details) this.details.field_layout = fields;
    },

    async updateCardValue(cardId, valueCents) {
      const { data } = await KanbanApi.updateCard(cardId, {
        value_cents: valueCents,
      });
      this.patchCardEverywhere(cardId, data);
      return data;
    },

    async lookupContactByPhone(phoneNumber) {
      const { data } = await KanbanApi.lookupContact(
        phoneNumber,
        this.activePipelineId
      );
      return data;
    },

    // Server decides the placement, so the created card is pushed into the column it
    // came back in rather than the one the dialog guessed.
    async createCard(payload) {
      const { data } = await KanbanApi.createCard(payload);
      const column = this.columns[data.stage_id];
      if (column) {
        // Top of the column, matching where the server placed it.
        column.cards.unshift(data);
        column.totalCount += 1;
      }
      return data;
    },

    // Removes the card from the board. The conversation stays — see the note on
    // the controller's destroy.
    async deleteCard(cardId) {
      await KanbanApi.deleteCard(cardId);
      Object.values(this.columns).forEach(column => {
        const index = column.cards.findIndex(card => card.id === cardId);
        if (index === -1) return;
        column.cards.splice(index, 1);
        column.totalCount = Math.max(0, column.totalCount - 1);
      });
      if (this.details?.id === cardId) this.closeDetails();
    },

    // The card's own name, overriding the contact's. Empty string clears it and the
    // card goes back to being named after the person.
    async updateCardTitle(cardId, title) {
      const { data } = await KanbanApi.updateCard(cardId, { title });
      this.patchCardEverywhere(cardId, data);
      if (this.details?.id === cardId) {
        this.details.title = data.title;
        this.details.display_name = data.display_name;
      }
      return data;
    },

    // The board payload carries only what a card renders; the drawer needs messages,
    // history and the summary, so it is a separate fetch on open.
    async fetchCardDetails(cardId) {
      if (this.moveRefusal && this.moveRefusal.cardId !== cardId)
        this.moveRefusal = null;
      this.uiFlags.fetchingDetails = true;
      try {
        const { data } = await KanbanApi.getCardDetails(cardId);
        this.details = data;
        return data;
      } finally {
        this.uiFlags.fetchingDetails = false;
      }
    },

    closeDetails() {
      this.details = null;
      this.moveRefusal = null;
      this.timeline = timelineVazia();
    },

    // CRM-04 R2-T7 (K2): a primeira página da linha do tempo do negócio. A resposta de um card que já
    // não está aberto (o painel trocou de card no meio) é descartada.
    async fetchTimeline(cardId) {
      this.timeline = { ...timelineVazia(), cardId, loading: true };
      try {
        const { data } = await KanbanApi.getCardTimeline(cardId);
        if (this.timeline.cardId !== cardId) return;
        this.timeline.items = data.items || [];
        this.timeline.nextCursor = data.next_cursor || null;
        this.timeline.hiddenCount = data.hidden_conversations_count || 0;
      } catch (error) {
        if (this.timeline.cardId === cardId) this.timeline.error = true;
        throw error;
      } finally {
        if (this.timeline.cardId === cardId) this.timeline.loading = false;
      }
    },

    // "Carregar mais": a página seguinte, pelo cursor do servidor, sem repetir item.
    async loadMoreTimeline() {
      const { cardId, nextCursor, loadingMore } = this.timeline;
      if (!nextCursor || loadingMore) return;
      this.timeline.loadingMore = true;
      this.timeline.error = false;
      try {
        const { data } = await KanbanApi.getCardTimeline(cardId, {
          before: nextCursor,
        });
        if (this.timeline.cardId !== cardId) return;
        const tem = new Set(this.timeline.items.map(chaveDoItem));
        this.timeline.items.push(
          ...(data.items || []).filter(item => !tem.has(chaveDoItem(item)))
        );
        this.timeline.nextCursor = data.next_cursor || null;
        if ('hidden_conversations_count' in data)
          this.timeline.hiddenCount = data.hidden_conversations_count;
      } catch (error) {
        if (this.timeline.cardId === cardId) this.timeline.error = true;
        throw error;
      } finally {
        if (this.timeline.cardId === cardId) this.timeline.loadingMore = false;
      }
    },

    // CRM-04 R2-T7 (N12): adicionar e tirar contato. Só a mudança: a recusa do servidor (409, 404, 422)
    // chega inteira a quem chamou, e reler o negócio é o `refreshDeal`, com o próprio recado de erro.
    async addCardContact(cardId, contactId) {
      const { data } = await KanbanApi.addCardContact(cardId, contactId);
      return data;
    },

    async removeCardContact(cardId, contactId) {
      const { data } = await KanbanApi.removeCardContact(cardId, contactId);
      return data;
    },

    // Os campos de um contato do negócio. O painel tem a lista inteira, e só esse contato muda nela.
    async updateCardContact(cardId, contactId, contact) {
      const { data } = await KanbanApi.updateCardContact(
        cardId,
        contactId,
        contact
      );
      if (this.details?.id === cardId) {
        const contatos = this.details.contacts || [];
        const index = contatos.findIndex(c => c.id === contactId);
        if (index !== -1) contatos[index] = { ...contatos[index], ...data };
      }
      return data;
    },

    // Relê o negócio depois de mexer nele: o painel, a linha do tempo e a face no quadro. Tirar um
    // contato devolve as conversas dele à regra de entrada, que pode criar negócio novo: com `board`, o
    // quadro aberto no funil do negócio relê também.
    async refreshDeal(cardId, { board = false } = {}) {
      const painel = await this.fetchCardDetails(cardId);
      this.patchCardEverywhere(cardId, faceDoPainel(painel));
      await this.fetchTimeline(cardId);
      if (board && painel.pipeline_id === this.activePipelineId)
        await this.fetchBoard();
      return painel;
    },

    async generateSummary(cardId) {
      this.uiFlags.generatingSummary = true;
      try {
        const { data } = await KanbanApi.generateSummary(cardId);
        if (this.details?.id === cardId) {
          this.details.summary = data.summary;
          this.details.summary_generated_at = data.generated_at;
        }
        return data;
      } finally {
        this.uiFlags.generatingSummary = false;
      }
    },

    patchCardEverywhere(cardId, patch) {
      Object.values(this.columns).forEach(column => {
        const card = column.cards.find(c => c.id === cardId);
        if (card) Object.assign(card, patch);
      });
    },
  },
});
